/**
 * Shared sieve-size helpers for procedure sieve_setup and similar UIs.
 */

import type { LeanSieveSize } from "@/types/central";
import type { SieveSize } from "@/types/central/gradation";

/** Default decimal places for sieve % when procedure omits percentage_precision. */
export const DEFAULT_PERCENTAGE_PRECISION = 2;
export const PERCENTAGE_PRECISION_MIN = 0;
export const PERCENTAGE_PRECISION_MAX = 4;

/** True when value is an integer in 0–4 (inclusive). */
export function isValidPercentagePrecision(
  value: number | null | undefined,
): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= PERCENTAGE_PRECISION_MIN &&
    value <= PERCENTAGE_PRECISION_MAX
  );
}

/**
 * Coerce null/undefined/missing to DEFAULT_PERCENTAGE_PRECISION.
 * Does not coerce floats or out-of-range ints — those fail Zod validation.
 */
export function normalizePercentagePrecision(
  value: number | null | undefined,
): number {
  if (value == null) return DEFAULT_PERCENTAGE_PRECISION;
  return value;
}

/**
 * Parse a percentage-precision input string.
 * - `""` → `null` (empty / not set)
 * - integer 0–4 → that integer
 * - anything else → `undefined` (reject / ignore)
 */
export function parsePercentagePrecisionInput(
  raw: string,
): number | null | undefined {
  if (raw === "") return null;
  if (!/^\d+$/.test(raw)) return undefined;
  const parsed = Number(raw);
  if (!isValidPercentagePrecision(parsed)) return undefined;
  return parsed;
}

/**
 * Resolve a stored value for display/blur: null/undefined → default;
 * invalid (float / out of range) → default; valid integer kept.
 */
export function resolvePercentagePrecision(
  value: number | null | undefined,
): number {
  if (isValidPercentagePrecision(value)) return value;
  return DEFAULT_PERCENTAGE_PRECISION;
}

/** Sort sieves coarsest → finest by diameter (mm). */
export function sortSievesByDiameterDesc(
  sieves: LeanSieveSize[],
): LeanSieveSize[] {
  return [...sieves].sort((a, b) => b.sieve_dia_mm - a.sieve_dia_mm);
}

/** Map a full sieve catalog item to the lean sieve-size shape. */
export function toLeanSieveSize(
  sieve: Pick<
    SieveSize,
    "uuid" | "sieve_standard" | "sieve_name" | "sieve_dia_mm" | "is_active"
  >,
): LeanSieveSize {
  return {
    uuid: sieve.uuid,
    sieve_standard: sieve.sieve_standard,
    sieve_name: sieve.sieve_name,
    sieve_dia_mm: sieve.sieve_dia_mm,
    is_active: sieve.is_active !== false,
    percentage_precision: DEFAULT_PERCENTAGE_PRECISION,
  };
}

/** Ensure each stack entry has an explicit integer percentage_precision (default 2). */
export function withNormalizedPercentagePrecision(
  sieves: LeanSieveSize[],
): LeanSieveSize[] {
  return sieves.map((s) => ({
    ...s,
    percentage_precision: resolvePercentagePrecision(s.percentage_precision),
  }));
}

/** UI mass-column decimal places (retained / cumulative mass). */
export const MASS_DISPLAY_DECIMALS = 2;

/** True for the three percentage columns on a sieve row. */
export function isPercentageSieveField(field: string): boolean {
  return (
    field === "pc_passing" ||
    field === "pc_retained" ||
    field === "pc_ind_retained"
  );
}

/**
 * Decimal places for a measured sieve-table cell.
 * Percentage columns use the row's reporting precision; mass columns stay at 2.
 */
export function decimalsForSieveMeasuredField(
  field: string,
  percentagePrecision?: number | null,
): number {
  if (isPercentageSieveField(field)) {
    return resolvePercentagePrecision(percentagePrecision);
  }
  return MASS_DISPLAY_DECIMALS;
}
